import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { adminAPI } from '../services/api';
import { Shield, LogOut } from 'lucide-react';
import AdminLoginScreen from './admin/AdminLoginScreen';
import UserSearchCard from './admin/UserSearchCard';
import AllUsersCard from './admin/AllUsersCard';
import PendingUpgradesCard from './admin/PendingUpgradesCard';
import MaintenanceCard from './admin/MaintenanceCard';

const PAGE_SIZE = 10;

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [searchEmail, setSearchEmail] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersLoading, setUsersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  const [toggleLoading, setToggleLoading] = useState(null);

  // Verify admin cookie on mount via /admin/me (no localStorage involved).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await adminAPI.me();
        if (!cancelled) setIsAuthenticated(true);
      } catch (e) {
        if (!cancelled) setIsAuthenticated(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      await adminAPI.login(password);
      setIsAuthenticated(true);
      setPassword('');
      toast.success('Admin login successful');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await adminAPI.logout();
    } catch (e) {
      console.warn('Admin logout request failed (clearing local state anyway):', e);
    }
    try {
      localStorage.removeItem('longevity_admin_token');
    } catch (_) {
      /* ignore */
    }
    setIsAuthenticated(false);
    setSearchResult(null);
    setUsers([]);
    toast.info('Logged out of admin panel');
  };

  const handleSearchUser = async (e) => {
    e.preventDefault();
    if (!searchEmail.trim()) return;

    setSearchLoading(true);
    setSearchResult(null);
    try {
      const user = await adminAPI.searchUser(searchEmail.trim());
      setSearchResult(user);
    } catch (error) {
      const message = error.response?.data?.detail || 'User not found';
      toast.error(message);
    } finally {
      setSearchLoading(false);
    }
  };

  const loadUsers = async (page = 0) => {
    setUsersLoading(true);
    try {
      const response = await adminAPI.listUsers(PAGE_SIZE, page * PAGE_SIZE);
      setUsers(response.users);
      setUsersTotal(response.total);
      setCurrentPage(page);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to load users';
      toast.error(message);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleToggleTier = async (email, currentTier) => {
    const newTier = currentTier === 'pro' ? 'free' : 'pro';
    setToggleLoading(email);
    try {
      const response = await adminAPI.toggleTier(email, newTier);
      toast.success(response.message);
      if (searchResult && searchResult.email === email) {
        setSearchResult(response.user);
      }
      setUsers((prev) => prev.map((u) => (u.email === email ? { ...u, tier: newTier } : u)));
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update tier';
      toast.error(message);
    } finally {
      setToggleLoading(null);
    }
  };

  if (!isAuthenticated) {
    return (
      <AdminLoginScreen
        password={password}
        setPassword={setPassword}
        onSubmit={handleLogin}
        loading={loginLoading}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 pt-16">
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
              <p className="text-sm text-gray-400">
                Process Income Blueprint purchases — toggle member accounts to Pro
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        <PendingUpgradesCard />

        <UserSearchCard
          searchEmail={searchEmail}
          setSearchEmail={setSearchEmail}
          onSearch={handleSearchUser}
          searchLoading={searchLoading}
          searchResult={searchResult}
          toggleLoading={toggleLoading}
          onToggleTier={handleToggleTier}
        />

        <AllUsersCard
          users={users}
          usersLoading={usersLoading}
          usersTotal={usersTotal}
          currentPage={currentPage}
          pageSize={PAGE_SIZE}
          onLoadUsers={loadUsers}
          onToggleTier={handleToggleTier}
          toggleLoading={toggleLoading}
        />

        <MaintenanceCard />
      </div>
    </div>
  );
};

export default Admin;
